export function formatResource(template: string, ...args: (string | number)[]): string {
  return template.replace(/{(\d+)}/g, (match, index) => {
    const replacement = args[+index]; // +index converts the string to a number
    return typeof replacement !== "undefined" ? replacement.toString() : match;
  });
}

export async function removeCategoryFromItem(categoryName: string): Promise<void> {
  return new Promise<void>((resolve) => {
    Office.context.mailbox.item.categories.removeAsync([categoryName], (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        console.log(`Successfully removed category '${categoryName}' from the item.`);
        resolve();
      } else {
        console.error(`Failed to remove category. Error: ${asyncResult.error.message}`);
        resolve(); // reject(new Error(asyncResult.error.message));
      }
    });
  });
}

export function addCategoryToItem(categoryName: string): Promise<void> {
  return new Promise<void>((resolve) => {
    // The category must already exist in the mailbox master list.
    // If it's not found, an InvalidCategory error will occur.
    Office.context.mailbox.item.categories.addAsync([categoryName], (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        console.log(`Successfully assigned category '${categoryName}' to the item.`);
        resolve();
      } else {
        console.error(`Failed to assign category. Error: ${asyncResult.error.message}`);
        resolve(); // reject(new Error(asyncResult.error.message));
      }
    });
  });
}

export function AddCategoriesToMasterTable(): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    // Define the default categories and their corresponding colors
    const categories = [
      { displayName: "Standard Encryption", color: Office.MailboxEnums.CategoryColor.Preset22 },
      { displayName: "Shared Key Encryption", color: Office.MailboxEnums.CategoryColor.Preset24 },
    ];

    // Get the existing categories from the Master Category Table
    Office.context.mailbox.masterCategories.getAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        const existingCategories = asyncResult.value;
        console.log(
          "Existing categories in Master Category Table:",
          existingCategories.map((c) => c.displayName)
        );

        // Filter out categories that are already present in the Master Category Table
        const newCategories = categories.filter(
          (category) =>
            !existingCategories.some((existingCategory) => existingCategory.displayName === category.displayName)
        );

        // If there are new categories to add, prepare them and add to the Master Category Table
        if (newCategories.length > 0) {
          Office.context.mailbox.masterCategories.addAsync(newCategories, (addResult) => {
            if (addResult.status === Office.AsyncResultStatus.Succeeded) {
              console.log(
                "Categories successfully added to the master category table:",
                newCategories.map((c) => c.displayName)
              );
              resolve(true); // Resolve the promise when categories are added successfully
            } else {
              console.error("Failed to add categories to master category table:", addResult.error);
              resolve(false); // Reject the promise on error
            }
          });
        } else {
          console.log("All specified categories are already present in the Master Category Table.");
          resolve(true); // Resolve the promise if no categories need to be added
        }
      } else {
        console.error("Failed to retrieve master category table:", asyncResult.error);
        resolve(false);
      }
    });
  });
}

// Helper function to create <t:SetItemField> for a single header
export function createSetItemFieldForHeader(headerKey: string, headerValue: string): string {
  return `
          <t:SetItemField>
            <t:ExtendedFieldURI DistinguishedPropertySetId="InternetHeaders" PropertyName="${headerKey}" PropertyType="String"/>
            <t:Message>
              <t:ExtendedProperty>
                <t:ExtendedFieldURI DistinguishedPropertySetId="InternetHeaders" PropertyName="${headerKey}" PropertyType="String"/>
                <t:Value>${headerValue}</t:Value>
              </t:ExtendedProperty>
            </t:Message>
          </t:SetItemField>
  `;
}

// Helper function to create <t:DeleteItemField> for a single header
export function createDeleteItemFieldForHeader(headerKey: string): string {
  return `
          <t:DeleteItemField>
            <t:ExtendedFieldURI DistinguishedPropertySetId="InternetHeaders" PropertyName="${headerKey}" PropertyType="String"/>
          </t:DeleteItemField>
  `;
}

// Helper function to create the categories SetItemField if categories are provided
export function createSetItemFieldForCategories(categories: string[]): string {
  if (categories.length === 0) {
    return "";
  }

  const categoryValues = categories.map((cat) => `<t:Value>${cat}</t:Value>`).join("\n");
  return `
          <t:SetItemField>
            <t:ExtendedFieldURI PropertySetId="00020329-0000-0000-C000-000000000046" PropertyName="Keywords" PropertyType="StringArray"/>
            <t:Message>
              <t:ExtendedProperty>
                <t:ExtendedFieldURI PropertySetId="00020329-0000-0000-C000-000000000046" PropertyName="Keywords" PropertyType="StringArray"/>
                <t:Values>
                  ${categoryValues}
                </t:Values>
              </t:ExtendedProperty>
            </t:Message>
          </t:SetItemField>
  `;
}

export function createDeleteItemPropertiesSoapRequest(
  changeKey: string,
  headersToDelete: string[],
  deleteCategories: boolean,
  itemID: string
): string {
  let updates = "";

  // Add delete fields for headers
  if (headersToDelete && headersToDelete.length > 0) {
    updates += headersToDelete.map((headerKey) => createDeleteItemFieldForHeader(headerKey)).join("");
  }

  if (deleteCategories) {
    updates += `
    <t:DeleteItemField>
      <t:ExtendedFieldURI PropertySetId="00020329-0000-0000-C000-000000000046" PropertyName="Keywords" PropertyType="StringArray"/>
    </t:DeleteItemField>
`;
  }

  const soapRequest = `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" 
               xmlns:xsd="http://www.w3.org/2001/XMLSchema" 
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/" 
               xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages" 
               xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types">
  <soap:Header>
    <t:RequestServerVersion Version="Exchange2013" xmlns="http://schemas.microsoft.com/exchange/services/2006/types" soap:mustUnderstand="0"/>
  </soap:Header>
  <soap:Body>
    <UpdateItem MessageDisposition="SaveOnly" ConflictResolution="AlwaysOverwrite" xmlns="http://schemas.microsoft.com/exchange/services/2006/messages">
      <ItemChanges>
        <t:ItemChange>
          <t:ItemId Id="${itemID}" ChangeKey="${changeKey}"/>
          <t:Updates>
            ${updates}
          </t:Updates>
        </t:ItemChange>
      </ItemChanges>
    </UpdateItem>
  </soap:Body>
</soap:Envelope>`;

  return soapRequest;
}

export function createUpdateItemSoapRequest(
  changeKey: string,
  headersToAddOrUpdate: { [key: string]: string },
  headersToDelete: string[],
  categoriesToUpdate: string[],
  itemID: string
): string {
  let updates = "";

  // Add or Update headers
  if (headersToAddOrUpdate && Object.keys(headersToAddOrUpdate).length > 0) {
    const setItemFieldsForHeaders = Object.entries(headersToAddOrUpdate)
      .map(([key, value]) => createSetItemFieldForHeader(key, value))
      .join("");
    updates += setItemFieldsForHeaders;
  }

  // Add or Update categories
  if (categoriesToUpdate && categoriesToUpdate.length > 0) {
    updates += createSetItemFieldForCategories(categoriesToUpdate);
  }

  // Delete headers
  if (headersToDelete && headersToDelete.length > 0) {
    const deleteItemFields = headersToDelete.map((headerKey) => createDeleteItemFieldForHeader(headerKey)).join("");
    updates += deleteItemFields;
  }

  const soapRequest = `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" 
               xmlns:xsd="http://www.w3.org/2001/XMLSchema" 
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/" 
               xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages" 
               xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types">
  <soap:Header>
    <t:RequestServerVersion Version="Exchange2013" xmlns="http://schemas.microsoft.com/exchange/services/2006/types" soap:mustUnderstand="0"/>
  </soap:Header>
  <soap:Body>
    <UpdateItem MessageDisposition="SaveOnly" ConflictResolution="AlwaysOverwrite" xmlns="http://schemas.microsoft.com/exchange/services/2006/messages">
      <ItemChanges>
        <t:ItemChange>
          <t:ItemId Id="${itemID}" ChangeKey="${changeKey}"/>
          <t:Updates>
            ${updates}
          </t:Updates>
        </t:ItemChange>
      </ItemChanges>
    </UpdateItem>
  </soap:Body>
</soap:Envelope>`;

  return soapRequest;
}

// Function to fetch the ChangeKey for a given item
export function GetChangeKey(itemId: string, callback: (changeKey: string) => void) {
  const getItemRequest = `
    <soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/" xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages" xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types">
      <soap:Header>
        <t:RequestServerVersion Version="Exchange2013" xmlns="http://schemas.microsoft.com/exchange/services/2006/types" soap:mustUnderstand="0"/>
      </soap:Header>
      <soap:Body>
        <GetItem xmlns="http://schemas.microsoft.com/exchange/services/2006/messages">
          <ItemShape>
            <t:BaseShape>IdOnly</t:BaseShape>
          </ItemShape>
          <ItemIds>
            <t:ItemId Id="${itemId}" />
          </ItemIds>
        </GetItem>
      </soap:Body>
    </soap:Envelope>`;

  Office.context.mailbox.makeEwsRequestAsync(getItemRequest, (asyncResult) => {
    if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
      console.log("Success in getting changeKey");
      const parser = new DOMParser();
      const response = parser.parseFromString(asyncResult.value, "text/xml");
      const changeKeyNode = response.getElementsByTagName("t:ItemId")[0];
      const changeKey = changeKeyNode ? changeKeyNode.getAttribute("ChangeKey") : null;
      callback(changeKey);
    } else {
      console.error("Error in GetChangeKey:", asyncResult.error);
      callback(null);
    }
  });
}

// First, create a promise-based wrapper for GetChangeKey
export function getChangeKeyAsync(itemID: string): Promise<string> {
  return new Promise((resolve, reject) => {
    GetChangeKey(itemID, (changeKey: string) => {
      if (changeKey) {
        resolve(changeKey);
      } else {
        reject(new Error("Failed to retrieve ChangeKey"));
      }
    });
  });
}

// Create a promise-based wrapper for makeEwsRequestAsync
export function makeEwsRequestAsyncPromise(soapRequest: string): Promise<string> {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.makeEwsRequestAsync(soapRequest, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        resolve(asyncResult.value);
      } else {
        reject(asyncResult.error);
      }
    });
  });
}

// Now define an async method that uses async/await, making the logic synchronous.
export async function updateItemProperties(
  headersToAddOrUpdate: { [key: string]: string },
  headersToDelete: string[],
  categories: string[],
  itemID: string
): Promise<void> {
  console.log("Retrieving changeKey...");

  // Wait until we get the changeKey
  const changeKey = await getChangeKeyAsync(itemID);
  console.log("Retrieved changeKey: ", changeKey);

  // Construct the soap request after we have the changeKey
  const soapRequest = createUpdateItemSoapRequest(changeKey, headersToAddOrUpdate, headersToDelete, categories, itemID);
  console.log("SoapRequest: ", soapRequest);

  // Wait until the EWS request completes
  await makeEwsRequestAsyncPromise(soapRequest);
  console.log("Success in updating item with soap request");
}

export async function RemoveItemProperties(
  headersToDelete: string[],
  deleteCategories: boolean,
  itemID: string
): Promise<void> {
  console.log("Removing encryption headers...");

  // Get the change key
  const changeKey = await getChangeKeyAsync(itemID);
  console.log("Retrieved changeKey: ", changeKey);

  // Create the SOAP request for deletion
  const soapRequest = createDeleteItemPropertiesSoapRequest(changeKey, headersToDelete, deleteCategories, itemID);
  console.log("SoapRequest: ", soapRequest);

  // Send the EWS request
  await makeEwsRequestAsyncPromise(soapRequest);

  console.log("Success in deleting enc properties with soap request");
}

export async function getCustomPropertyValue(propertyName: string): Promise<string | null> {
  // Load the custom properties
  const customProps = await loadCustomProperties();

  // Get the property value
  const value = customProps.get(propertyName);

  // Return the value or null if not found
  return value ?? null;
}

// This function uses async/await to handle asynchronous calls in a sequential manner.
export async function setCustomProperty(name, value) {
  // Load custom properties
  const customProps = await loadCustomProperties();

  // Set the custom property
  customProps.set(name, value);

  // Save the custom properties
  await saveCustomProperties(customProps);
}

// Loads CustomProperties for the current item.
export function loadCustomProperties(): Promise<Office.CustomProperties> {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.loadCustomPropertiesAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        resolve(asyncResult.value);
      } else {
        reject(new Error("Failed to load custom properties."));
      }
    });
  });
}

// Saves CustomProperties back to the server.
export function saveCustomProperties(customProps: Office.CustomProperties): Promise<void> {
  return new Promise((resolve, reject) => {
    customProps.saveAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        resolve();
      } else {
        reject(new Error("Failed to save custom properties."));
      }
    });
  });
}

/**
 * Deletes a custom property if present.
 *
 * @param name The property name to delete.
 * @throws If the property does not exist.
 */
export async function deleteCustomProperty(name: string): Promise<void> {
  // Load existing custom properties
  const customProps = await loadCustomProperties();
  // Verify existence
  const existing = customProps.get(name);
  if (existing === undefined) {
    console.log("Custom property: " + name + " does not exist.");
  }
  // Remove the property
  customProps.remove(name);
  // Persist deletion
  await saveCustomProperties(customProps);
}

export function RemoveHeaders(headerKeys: string[]) {
  new Promise<void>((resolve, reject) => {
    Office.context.mailbox.item.internetHeaders.removeAsync(headerKeys, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        console.log("Headers removed successfully:", headerKeys);
        resolve();
      } else {
        console.error("Removing headers failed:", asyncResult.error);
        reject("Removing headers failed " + asyncResult.error);
      }
    });
  });
}

/**
 * Defines a single custom-property operation.
 *
 * @property action  The type of operation: 'get', 'set', or 'delete'.
 * @property name    The property name to act on.
 * @property value   (Optional) The value for 'set' operations.
 */
export interface CustomPropertyOperation {
  action: "get" | "set" | "delete";
  name: string;
  value?: string;
}

/**
 * Builds a list of get/set/delete operations for custom properties.
 *
 * @param getNames    Array of property names to read.
 * @param setHeaders  Map of property names to values to add/update.
 * @param deleteNames Array of property names to remove.
 * @returns A Promise resolving to the combined CustomPropertyOperation[].
 */
export function generateCustomPropertyOperations(
  getNames: string[],
  setHeaders: Record<string, string>,
  deleteNames: string[]
): Promise<CustomPropertyOperation[]> {
  const operations: CustomPropertyOperation[] = [];

  // 1) 'get' operations
  for (const name of getNames) {
    operations.push({ action: "get", name });
  }

  // 2) 'set' operations (add or update)
  for (const [name, value] of Object.entries(setHeaders)) {
    operations.push({ action: "set", name, value });
  }

  // 3) 'delete' operations
  for (const name of deleteNames) {
    operations.push({ action: "delete", name });
  }

  return Promise.resolve(operations);
}

/**
 * Performs batch get, set, and delete operations on custom properties.
 *
 * @param operations Array of CustomPropertyOperation items.
 * @returns A promise resolving to a map of property names → values for all 'get' operations.
 */
export async function batchCustomProperties(operations: CustomPropertyOperation[]): Promise<Record<string, string>> {
  // Load once
  const customProps = await loadCustomProperties();
  const result: Record<string, string> = {};
  const hasChanges = operations.some((op) => op.action !== "get");

  for (const op of operations) {
    switch (op.action) {
      case "get": {
        // Now scoped within its own block
        const existing = customProps.get(op.name);
        if (existing !== undefined) {
          result[op.name] = existing;
        }
        break;
      }
      case "set": {
        customProps.set(op.name, op.value!);
        break;
      }
      case "delete": {
        customProps.remove(op.name);
        break;
      }
    }
  }

  if (hasChanges) {
    await saveCustomProperties(customProps);
  }

  return result;
}

/**
 * Retrieves all internet headers (built-in + custom) for the current message in Read mode.
 * Logs them and returns a name→value map.
 */
export function getAllInternetHeaders(): Promise<Record<string, string>> {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.getAllInternetHeadersAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        const raw = asyncResult.value as string;
        const headers: Record<string, string> = {};

        if (raw && raw.trim()) {
          raw.split(/\r?\n/).forEach((line) => {
            const idx = line.indexOf(":");
            if (idx > 0) {
              const name = line.substring(0, idx).trim();
              const value = line.substring(idx + 1).trim();
              headers[name] = value;
              console.log(`${name}: ${value}`);
            }
          });
        } else {
          console.log("No internet headers found on this item.");
        }

        resolve(headers);
      } else {
        console.error("getAllInternetHeadersAsync failed:", asyncResult.error);
        reject(asyncResult.error);
      }
    });
  });
}

export function GetHeaders(headerKeys: string[]): Promise<Record<string, string>> {
  console.log("HeaderKeys: " + headerKeys);
  return new Promise<Record<string, string>>((resolve, reject) => {
    Office.context.mailbox.item.internetHeaders.getAsync(headerKeys, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        const headers: Record<string, string> = asyncResult.value || {};
        console.log("Headers retrieved successfully:", headers);
        resolve(headers);
      } else {
        console.error("Getting headers failed:", asyncResult.error);
        reject("Getting headers failed: " + asyncResult.error.message);
      }
    });
  });
}

export function AddHeaders(headers: { [key: string]: string }) {
  new Promise<void>((resolve, reject) => {
    Office.context.mailbox.item.internetHeaders.setAsync(headers, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        console.log("Headers added successfully:", headers);
        resolve();
      } else {
        console.error("Adding headers failed:", asyncResult.error);
        reject("Adding headers failed " + asyncResult.error);
      }
    });
  });
}

export function showNotification(message: string) {
  // Ensure the host is Outlook and the item is in a compose scenario.
  if (Office.context.mailbox && Office.context.mailbox.item) {
    Office.context.mailbox.item.notificationMessages.addAsync(
      "encryptionNotification", // A unique key to identify the notification.
      {
        type: "informationalMessage", // Other option: "errorMessage"
        icon: "Icon.80x80",
        message: message,
        persistent: false, // If true, the notification must be dismissed manually
      },
      function (asyncResult) {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("Failed to show notification:", asyncResult.error.message);
        } else {
          console.log("Notification shown successfully.");
        }
      }
    );
  } else {
    console.error("Not in an Outlook compose scenario or mailbox not accessible.");
  }
}

export function removeNotification() {
  // Use the same notification ID that was used when the notification was added
  Office.context.mailbox.item.notificationMessages.removeAsync("encryptionNotification", function (asyncResult) {
    if (asyncResult.status === Office.AsyncResultStatus.Failed) {
      console.error("Failed to remove notification:", asyncResult.error.message);
    } else {
      console.log("Notification removed successfully.");
    }
  });
}

/**
 * Detects if the Outlook add-in is connected to a Microsoft 365 Exchange Online server.
 * @returns {boolean} True if connected to M365 Exchange Online, false otherwise.
 */
export function isConnectedToM365Exchange() {
  // Ensure Office.js is loaded and mailbox context is available
  if (typeof Office === "undefined" || !Office.context || !Office.context.mailbox) {
    console.log("Office.js or Mailbox context not fully loaded. Cannot determine Exchange environment.");
    return false;
  }

  // The most reliable way is to check the REST API URL
  if (Office.context.mailbox.restUrl) {
    const restUrl = Office.context.mailbox.restUrl.toLowerCase();

    // Common patterns for Exchange Online REST URLs
    const m365Patterns = [
      "https://outlook.office.com/api",
      "https://outlook.office365.com/api",
      // Add other potential M365 patterns if you encounter them (e.g., regional ones)
      // For example: "https://outlook.office.de/api" for Germany, etc.
    ];

    for (const pattern of m365Patterns) {
      if (restUrl.startsWith(pattern)) {
        return true;
      }
    }
  } else {
    console.log("Office.context.mailbox.restUrl is not available. Cannot determine Exchange environment.");
    // If restUrl is not available, it might indicate an older client or specific environment
    // For a boolean return, treating this as "not M365" is safer, or you might throw an error.
    return false;
  }

  // If none of the M365 patterns match, assume it's on-premises or an unknown environment
  return false;
}

export enum OutlookClientHost {
  ClassicOutlookWindows = "Classic Outlook for Windows",
  NewOutlookWindows = "New Outlook for Windows",
  OutlookWeb = "Outlook on the web",
  OutlookMac = "Outlook for Mac",
  OutlookIOS = "Outlook for iOS",
  OutlookAndroid = "Outlook for Android",
  Unknown = "Unknown Outlook Host",
}

/**
 * Determines the specific Outlook client host where the add-in is running.
 * It uses the Office.context.diagnostics API to differentiate between clients
 * like Classic Outlook, New Outlook for Windows, OWA, etc.
 * @returns The specific OutlookClientHost enum member.
 */
export async function getOutlookClientHost(): Promise<OutlookClientHost> {
  // Use the more specific `hostName` from mailbox diagnostics, which is the recommended approach.
  const hostName = Office.context.mailbox.diagnostics.hostName;

  switch (hostName) {
    case "OutlookWebApp":
      return OutlookClientHost.OutlookWeb;
    case "newOutlookWindows":
      return OutlookClientHost.NewOutlookWindows;
    case "Outlook":
      // The hostName 'Outlook' is used for both classic Windows and Mac.
      // We use `Office.context.diagnostics.platform` to differentiate.
      if (Office.context.diagnostics.platform === Office.PlatformType.Mac) {
        return OutlookClientHost.OutlookMac;
      }
      return OutlookClientHost.ClassicOutlookWindows;
    case "OutlookIOS":
      return OutlookClientHost.OutlookIOS;
    case "OutlookAndroid":
      return OutlookClientHost.OutlookAndroid;
    default:
      return OutlookClientHost.Unknown;
  }
}
